/**
 * Pure helpers for PriceListBulkPropertyDialog (selection sync, pagination,
 * Romanian numeric input sanitization).
 */

/**
 * Pagination options for the bulk dialog product table.
 * Intentionally omits Vuetify's default `-1` / "All" — rendering every matched
 * row plus pricing preview freezes the UI on large price lists.
 */
export const BULK_DIALOG_ITEMS_PER_PAGE_OPTIONS = Object.freeze([
    { value: 10, title: '10' },
    { value: 25, title: '25' },
    { value: 50, title: '50' },
    { value: 100, title: '100' },
]);

/** Debounce (ms) before rebuilding the expensive per-product pricing preview. */
export const BULK_DIALOG_PREVIEW_DEBOUNCE_MS = 250;

/** Max fraction digits for bulk „Valoare nouă” / % inputs. */
export const BULK_NUMERIC_INPUT_MAX_DECIMALS = 2;

/**
 * Sanitize bulk numeric input to Romanian style:
 * - decimal separator is comma only (periods are stripped — never kept as decimals)
 * - at most `maxDecimals` digits after the comma
 * - optional leading minus
 *
 * Allows incomplete typing states: "", "-", ",", "-,".
 *
 * @param {unknown} raw
 * @param {{ maxDecimals?: number, allowNegative?: boolean }} [options]
 * @returns {string}
 */
export function sanitizeBulkNumericInput(
    raw,
    {
        maxDecimals = BULK_NUMERIC_INPUT_MAX_DECIMALS,
        allowNegative = true,
    } = {},
) {
    if (raw === null || raw === undefined) {
        return '';
    }
    let text = String(raw).replace(/\s/g, '');
    if (!text) {
        return '';
    }

    let negative = false;
    if (text.startsWith('-')) {
        if (!allowNegative) {
            text = text.slice(1);
        } else {
            negative = true;
            text = text.slice(1);
        }
    }

    // Period is not allowed as decimal separator. On paste of EN values like
    // "12.5", treat a single trailing/fractional dot as comma; otherwise strip.
    if (text.includes('.') && !text.includes(',')) {
        const dotCount = (text.match(/\./g) || []).length;
        if (dotCount === 1) {
            text = text.replace('.', ',');
        } else {
            text = text.replace(/\./g, '');
        }
    } else {
        text = text.replace(/\./g, '');
    }

    // Keep digits and commas only; collapse to a single decimal comma.
    text = text.replace(/[^\d,]/g, '');
    const commaIndex = text.indexOf(',');
    if (commaIndex >= 0) {
        const intPart = text.slice(0, commaIndex).replace(/,/g, '');
        const fracPart = text
            .slice(commaIndex + 1)
            .replace(/,/g, '')
            .slice(0, Math.max(0, maxDecimals));
        text = `${intPart},${fracPart}`;
    } else {
        text = text.replace(/,/g, '');
    }

    if (!text) {
        return negative ? '-' : '';
    }
    return negative ? `-${text}` : text;
}

/**
 * Whether a keydown should be blocked for bulk numeric inputs
 * (period forbidden; max 2 decimals after comma).
 *
 * @param {KeyboardEvent} event
 * @param {string} currentValue
 * @returns {boolean} true = preventDefault
 */
export function shouldBlockBulkNumericKey(event, currentValue = '') {
    const key = event.key;
    if (!key) {
        return false;
    }
    // Allow navigation / editing / shortcuts.
    if (
        key === 'Backspace'
        || key === 'Delete'
        || key === 'Tab'
        || key === 'Enter'
        || key === 'Escape'
        || key === 'ArrowLeft'
        || key === 'ArrowRight'
        || key === 'ArrowUp'
        || key === 'ArrowDown'
        || key === 'Home'
        || key === 'End'
        || event.ctrlKey
        || event.metaKey
        || event.altKey
    ) {
        return false;
    }
    // Period / numpad decimal — never allowed (only comma).
    if (key === '.' || key === 'Decimal') {
        return true;
    }
    // Non-character keys (F1, etc.)
    if (key.length !== 1) {
        return false;
    }
    if (!/[\d,-]/.test(key)) {
        return true;
    }
    const current = String(currentValue ?? '');
    const target = event.target;
    const start = typeof target?.selectionStart === 'number'
        ? target.selectionStart
        : current.length;
    const end = typeof target?.selectionEnd === 'number'
        ? target.selectionEnd
        : current.length;
    const proposed = `${current.slice(0, start)}${key}${current.slice(end)}`;
    const sanitized = sanitizeBulkNumericInput(proposed);
    // Key would be dropped by sanitizer (e.g. 3rd decimal digit) → block.
    return sanitized === current && proposed !== current;
}

/**
 * Sync checkbox selection when the matched product set changes (filters).
 *
 * Never auto-checks rows: keep only previously selected codes that are still
 * in the matched set. User must select explicitly (or use "Selectează toate").
 *
 * @param {string[]} selectedCodes
 * @param {Array<{ product_code?: string }>} matchedItems
 * @returns {string[]}
 */
export function syncBulkMatchedSelection(selectedCodes, matchedItems) {
    const codeSet = new Set(
        (matchedItems || [])
            .map((item) => item?.product_code)
            .filter(Boolean),
    );
    const selected = Array.isArray(selectedCodes) ? selectedCodes : [];
    return selected.filter((code) => codeSet.has(code));
}
